import * as React from 'react';
import { KeyboardEvent } from 'react';
import { 
  TabStrip, 
  TabStripSelectEventArguments, 
  TabStripTab 
} from '@progress/kendo-react-layout';
import { Component } from '../../dispatcher/modules';
import PuzzleDropDownButton from '../UI/button/dropdown/PuzzleDropDownButton';
import { DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import PuzzleTabStrip from '../UI/tab_strip/PuzzleTabStrip';
import PuzzleTooltip from '../UI/tooltip/PuzzleTooltip';
import WithTooltip from '../../utility/withTooltip';
import { isEqualWithoutFunctions } from '../../utility/Util';
import { Keys } from '@progress/kendo-react-common';
import PuzzleButton from '../UI/button/PuzzleButton';
import { 
  GET_TITLE_PROPS, 
  LAST_ACTION, 
  TAB_PANEL_ITEM, 
  TAB_PANEL_PROPS, 
  TAB_PANEL_STATE 
} from './Types';
import * as _ from 'lodash';

class TabPanel extends Component<TAB_PANEL_PROPS, TAB_PANEL_STATE> {
  puzzleTabStripRef: any = React.createRef();
  tabStripRef: any = React.createRef();
  dropDownButtonRef: any = React.createRef();
  /**
   * Tooltip for title
   * {any} @private
   */
  private tooltip:any = React.createRef();

  private numOfVisibleTitles:number = 0;
  private liOfDropDownWidth: number = 56;
  private isDropDownActive:boolean = false;
  private lastAction: LAST_ACTION = {
    type: '',
    index: 0
  };
  private numOfCreated:number = 0;

  private dropDownSelector: any = {
    content: <div></div>,
    title: '',
    config: {},
    isDropDownSelector: true
  };

  state: TAB_PANEL_STATE = {
    selected: 0,
    items: [],
    reorderingItemsInProgress: false
  }
  private items: TAB_PANEL_ITEM[] = [];
  constructor(props:TAB_PANEL_PROPS) {
    super(props);
    const { selected, items } = this.props;
    const newItems = [...items, this.dropDownSelector].map((item) => {
      this.numOfCreated++;
      return {
        ...item,
        numOfCreated: this.numOfCreated
      };
    });

    this.numOfVisibleTitles = newItems.length - 1;
    this.items = _.cloneDeep(newItems);

    this.state = {
      selected,
      items: _.cloneDeep(newItems),
      reorderingItemsInProgress: false
    };
  }

  shouldComponentUpdate(nextProps: Readonly<TAB_PANEL_PROPS>, nextState: Readonly<TAB_PANEL_STATE>): boolean {
    const { items = [] } = this.props;
    if (items.length && nextProps?.items?.length && !isEqualWithoutFunctions(items, nextProps.items)) {
      this.items = _.cloneDeep(nextProps.items);
      this.setState({
        items: _.cloneDeep(this.items)
      });
      return false;
    }

    if (nextState?.items?.length && this.shouldReorderItems(nextState.items.length)) {
      this.handleReorderingItems();
    }
    return true;
  }

  /**
   * Return true if li element is drop down
   * @param {HTMLLIElement} li
   */
  private isDropDownSelected = (li: HTMLLIElement):boolean => {
    const name = li?.children[0]?.children[0]?.attributes['name']?.value;

    return name === 'drop-down-tool-tip';
  }

  /**
   * Check if there is a need for reordering items
   * @param {number} nextNumOfTabs
   */
  private shouldReorderItems = (nextNumOfTabs: number):boolean => {
    const { type, index: actionIndex } = this.lastAction;
    if (type !== '') {
      const { current } = this.tabStripRef;
      const { _element: element } = current;
      const { clientWidth: withOfUl, firstChild } = element;
      const headerTabs = firstChild.children[0].children;
      const { items, reorderingItemsInProgress } = this.state;
      const { length: numOfTabs } = items;

      if ((type === 'addTabItem' && headerTabs[numOfTabs - 1] && nextNumOfTabs === numOfTabs)
        || (type === 'selectedExistedTab' && !this.isDropDownSelected(headerTabs[actionIndex]))
        || (type === 'deletedTab' && reorderingItemsInProgress)) {
        return true;
      } else if ((type === 'addTabItem' && headerTabs[numOfTabs - 1] && nextNumOfTabs !== numOfTabs)
        || (type === 'selectedExistedTab' && this.isDropDownSelected(headerTabs[actionIndex]))
        || (type === 'deletedTab' && nextNumOfTabs === numOfTabs && this.isDropDownActive)) {
        this.setState({
          reorderingItemsInProgress: true
        });
      } else if (reorderingItemsInProgress) {
        this.setState({ reorderingItemsInProgress: false });
      }
    }
    return false;
  }

  /**
   * Returns width of list items, width of unordered list and index of DropDown
   */
  private getTabParams = ():{ tabsWidths:number[], withOfUl:number, indexOfDropDown:number} => {
    const { current } = this.tabStripRef;
    const { _element: element } = current;
    const { clientWidth: withOfUl, children } = element.firstChild.children[0];
    let tabsWidths:number[] = [];
    let totalTabsWidth: number = 0;
    let indexOfDropDown: number = 1;

    let i: number = 0;
    for (let item of children) {
      if (this.isDropDownSelected(item)) {
        indexOfDropDown = i;
        item.className = `k-item k-last ${this.isDropDownActive ? 'tp-drop-down-selector' : 'tp-drop-down-selector-hidden'}`;
      }
      const clientWidth = item?.clientWidth;
      const tabWidth = clientWidth ? clientWidth : 0;
      tabsWidths = [...tabsWidths, tabWidth];
      totalTabsWidth = totalTabsWidth + tabWidth + 1;
      this.isDropDownActive = (withOfUl + this.liOfDropDownWidth) < totalTabsWidth;
      i++;
    }

    return {
      tabsWidths,
      withOfUl,
      indexOfDropDown
    };
  }

  /**
   * Reordering items after add item, remove item and selected existed item
   */
  private handleReorderingItems = ():void => {
    const { selected } = this.state;
    const items = _.cloneDeep(this.state.items);
    const { current } = this.tabStripRef;
    const { type } = this.lastAction;

    if (current) {
      const lastIsDropDownActive = this.isDropDownActive;
      const { tabsWidths, withOfUl, indexOfDropDown } = this.getTabParams();

      if (this.isDropDownActive) {
        let visibleTabs = items.slice(0, indexOfDropDown);
        let hiddenTabs = items.slice(indexOfDropDown + 1, items.length);
        let widthsOfVisibleTabs = tabsWidths.slice(0, indexOfDropDown);

        const totalWidthsOfVisibleTabs = widthsOfVisibleTabs.reduce((a, b) => a + b + 1, 0);
        if (type === 'deletedTab') {
          const isDeletedLastVisibleTab = selected === indexOfDropDown;
          let isCalcComplete = (totalWidthsOfVisibleTabs + this.liOfDropDownWidth + tabsWidths[indexOfDropDown + 1]) > withOfUl;
          let i = 1;
          while (!isCalcComplete) {
            widthsOfVisibleTabs = [...widthsOfVisibleTabs, tabsWidths[indexOfDropDown + i]];
            visibleTabs = [...visibleTabs, hiddenTabs[0]];
            hiddenTabs.splice(0, 1);
            const totalWidthsOfVisibleTabsWithOutSecond = widthsOfVisibleTabs.reduce((a, b) => a + b + 1, 0);
            i++;
            isCalcComplete = (totalWidthsOfVisibleTabsWithOutSecond + this.liOfDropDownWidth + tabsWidths[indexOfDropDown + i] + (isDeletedLastVisibleTab ? 28 : 0)) > withOfUl;
          }
        } else {
          let isCalcComplete = totalWidthsOfVisibleTabs < (withOfUl - this.liOfDropDownWidth);
          while (!isCalcComplete) {
            widthsOfVisibleTabs = [widthsOfVisibleTabs[0], ...widthsOfVisibleTabs.slice(2, widthsOfVisibleTabs.length)];
            hiddenTabs.push(visibleTabs[1]);
            visibleTabs = [visibleTabs[0], ...visibleTabs.slice(2, visibleTabs.length)];
            const totalWidthsOfVisibleTabsWithOutSecond = widthsOfVisibleTabs.reduce((a, b) => a + b + 1, 0);
            isCalcComplete = totalWidthsOfVisibleTabsWithOutSecond < (withOfUl - this.liOfDropDownWidth);
          }
        }
        this.numOfVisibleTitles = visibleTabs.length;
        const selectedAfterAction = type === 'deletedTab' ? selected : this.numOfVisibleTitles - 1;
        this.items = [...visibleTabs, items[indexOfDropDown], ...hiddenTabs.sort((a, b) => a['numOfCreated'] - b['numOfCreated'])];

        this.setState({
          selected: selectedAfterAction,
          items: _.cloneDeep(this.items),
          reorderingItemsInProgress: false
        }, () => {
          this.puzzleTabStripRef.current.setAccessibility();
          this.focusListItem(selectedAfterAction);
        });
      } else if (type === 'deletedTab' && lastIsDropDownActive && !this.isDropDownActive) {
        const indexOfDropDown = tabsWidths.indexOf(this.liOfDropDownWidth);
        const isDeletedLastVisibleTab = selected === indexOfDropDown;
        const dropDown = items[indexOfDropDown];
        this.items.splice(indexOfDropDown, 1);
        this.items.push(dropDown);
        this.numOfVisibleTitles = this.items.length - 1;
        const selectedAfterDeletedTab = isDeletedLastVisibleTab ? this.numOfVisibleTitles - 1 : selected;

        this.setState({
          selected: selectedAfterDeletedTab,
          items: _.cloneDeep(this.items),
          reorderingItemsInProgress: false
        }, () => {
          this.puzzleTabStripRef.current.setAccessibility();
          this.focusListItem(selectedAfterDeletedTab);
        });
      } else {
        this.puzzleTabStripRef.current.setAccessibility(this.numOfVisibleTitles + 1);
        this.focusListItem(selected);

        this.setState({
          reorderingItemsInProgress: false
        });
      }

      this.lastAction = { type: '', index: null };
    }
  }

  /**
   * Updates tab details in TabPanel by uniqueItemValue
   * @param tabDetails
   */
  handleUpdateTabDetails = (tabDetails: {newTitleName: string, newRequestUrl: string, uniqueItemValue: string}) => {
    const { newTitleName, newRequestUrl, uniqueItemValue } = tabDetails;
    const { items } = this;

    let updatedArray = items.map((item: TAB_PANEL_ITEM) => {
      let { config: { requestConfig } } = item;

      if (item.uniqueItemValue === uniqueItemValue) {
        item.title = newTitleName;

        if (newRequestUrl) {
          requestConfig.url = requestConfig.url !== newRequestUrl ? newRequestUrl : requestConfig.url;
        }
      }

      return { ...item };
    });

    this.setState({ items: updatedArray });
  }

  /**
   * Adds tab item to TabPanel
   * @param addedItem
   * @param renderType
   */
  addTabItem = (addedItem, renderType):void => {
    if (addedItem.config && this.props?.tabStripProps?.keepTabsMounted) {
      addedItem.config.moduleId = `module_id_${addedItem.uniqueItemValue}`;
    }
    addedItem.renderType = renderType;
    const { items } = this;
    let tabAlreadyOpen = false;
    let i = 0;
    for (let item of items) {
      if (addedItem.uniqueItemValue && addedItem?.uniqueItemValue === item?.uniqueItemValue) {
        tabAlreadyOpen = true;
        if (this.isDropDownActive && this.numOfVisibleTitles < i) {
          const selectedItem = item;

          this.items.splice(i, 1);
          this.items.splice(this.numOfVisibleTitles, 0, selectedItem);
          this.openTabOnDblClick(this.numOfVisibleTitles);
          this.lastAction = {
            type: 'selectedExistedTab',
            index: this.numOfVisibleTitles
          };
          this.numOfVisibleTitles = this.numOfVisibleTitles + 1;
        } else {
          this.openTabOnDblClick(i);
          this.focusListItem(i);
        }
      }
      i++;
    }

    if (!tabAlreadyOpen) {
      this.numOfCreated++;
      this.items.splice(this.numOfVisibleTitles, 0, {
        ...addedItem,
        numOfCreated: this.numOfCreated
      });

      this.lastAction = {
        type: 'addTabItem',
        index: this.numOfVisibleTitles
      };
      this.numOfVisibleTitles = this.numOfVisibleTitles + 1;

      this.openTabOnDblClick(this.numOfVisibleTitles - 1);
    }
  }

  /**
   * Opens tab on double click
   * @param {Number} counter
   */
  openTabOnDblClick = (counter:number):void => {
    const items = _.cloneDeep(this.items);
    const newStateObj = {
      selected: counter,
      items
    };
    this.setState(newStateObj, () => {
      if (this.props.onSelect) {
        this.props.onSelect(
          {
            item: items[counter]
          }
        );
      }
      if (this.props.onReconfigure) {
        const { columns } = items[counter].config;
        this.props.onReconfigure({ columns });
      }
    });
  }

  /**
   * Sets new selected state and triggers onSelect props if there is
   * @param {TabStripSelectEventArguments} e
   */
  private handleSelect = (e: TabStripSelectEventArguments):void => {
    const selected = e.selected;
    if (selected < this.numOfVisibleTitles) {
      this.setState({ selected }, () => {
        if (this.props.onSelect) {
          this.props.onSelect(
            {
              item: this.items[selected]
            }
          );
        }
        if (this.props.onReconfigure) {
          const { columns } = this.items[selected].config;
          this.props.onReconfigure({ columns });
        }
      });
    }
  }

  /**
   * Returns selected tab unique value
   */
  public getActiveTabUniqueValue = (): string => this.state.items[this.state.selected]?.uniqueItemValue

  /**
   * Add selected item on visible side of UI
   * @param {DropDownButtonItemClickEvent} e
   */
  private handleDropDownSelect = (e:DropDownButtonItemClickEvent):void => {
    const { itemIndex } = e;
    const indexOfSelectedTab = itemIndex + this.numOfVisibleTitles + 1;
    const selectedItem = this.items[indexOfSelectedTab];

    this.items.splice(indexOfSelectedTab, 1);
    this.items.splice(this.numOfVisibleTitles, 0, selectedItem);
    this.openTabOnDblClick(this.numOfVisibleTitles);
    this.lastAction = {
      type: 'selectedExistedTab',
      index: this.numOfVisibleTitles
    };
    this.numOfVisibleTitles = this.numOfVisibleTitles + 1;
  }

  /**
   * If some tab item conf has been changed this method set that changed
   * @param {any} cfg
   */
  private reconfigureActiveItem = (cfg: any):void => {
    this.items[this.state.selected].config = {
      ...this.items[this.state.selected].config,
      ...cfg
    };
    if (this.props.onReconfigure) {
      this.props.onReconfigure(cfg);
    }

    this.setState({ items: _.cloneDeep(this.items) });
  }

  /**
   * Returns the moduleId of the active grid.
   * @returns {string}
   */
  public getModuleIdOfActiveGrid = (): string => {
    // Get the active tab's unique item value
    const activeTabUniqueValue = this.getActiveTabUniqueValue();

    // Find the active item in the array of items based on the uniqueItemValue
    const activeItem = this.state.items.find(item => item.uniqueItemValue === activeTabUniqueValue);

    // Return the moduleId of the active grid
    return activeItem?.config?.moduleId || this.props.dashboardGridModuleId;
  }

  /**
   * Calc index of next selected item
   * @param {number} index
   * @param {number} selected
   * @param {number} numOfTabs
   */
  private getSelectedAfterClosed = (index:number, selected:number, numOfTabs:number):number => {
    // Is the removed tab to the left of the selected one
    if (index < selected) {
      return selected - 1;
    } else {
      return selected < numOfTabs ? selected : selected - 1;
    }
  }

  /**
   * Closable tab implementation
   * @param {number} index
   */
  private removeTab = (index:number):void => {
    const { selected } = this.state;
    const removedTabItems = this.items.splice(index, 1);

    let selectedAfterClosed = this.getSelectedAfterClosed(index, selected, this.items.length - 1);

    let selectedTabAfterClosed = this.items[selectedAfterClosed];
    if (_.isObject(selectedTabAfterClosed?.config?.requestConfig) && this.props.getActiveTabParams && selectedTabAfterClosed.uniqueItemValue) {
      let params = _.isObject(selectedTabAfterClosed.config.requestConfig.paramsOnMounting) ? selectedTabAfterClosed.config.requestConfig.paramsOnMounting : {};
      params = {
        ...params,
        ...(this.props.getActiveTabParams(selectedTabAfterClosed.uniqueItemValue)?.params || {})
      };
      selectedTabAfterClosed.config.requestConfig.paramsOnMounting = params;
    }
      
    this.lastAction = {
      type: 'deletedTab',
      index: selectedAfterClosed
    };

    if (!this.isDropDownActive) {
      this.numOfVisibleTitles--;
    }

    this.setState({
      items: _.cloneDeep(this.items),
      selected: selectedAfterClosed
    }, () => {
      // Get the selected tab after state update
      const selectedTab = this.items[selectedAfterClosed];

      if (!_.isEmpty(selectedTab?.config?.requestConfig?.paramsOnMounting)) {
        selectedTab.config.requestConfig.paramsOnMounting = {};
      }

      if (this.props.onSelect && (selected !== selectedAfterClosed || this.props.tabStripProps.keepTabsMounted)) {
        this.props.onSelect({
          item: selectedTab
        });
      }

      if (!this.isDropDownActive) {
        this.puzzleTabStripRef.current.setAccessibility(this.numOfVisibleTitles + 1);
        this.focusListItem(selectedAfterClosed);
      }

      if (this.props.onReconfigure) {
        const { columns } = selectedTab.config;
        this.props.onReconfigure({ columns });
      }
    });

    this.props.onTabRemoved?.(removedTabItems[0]);
  };

  /**
   * Focus li
   * @param {number} index
   */
  private focusListItem = (index:number):void => {
    const { current } = this.tabStripRef;
    const { _element: element } = current;
    const { children } = element.firstChild.children[0];

    for (let item of children) {
      item.removeAttribute('tabindex');
    }

    children[index].setAttribute('tabindex', '-1');
    children[index].focus();
    this.puzzleTabStripRef.current.tabFocus = index;
  }

  /**
   * Create a title
   * @param {string} content
   * @param {Function} onTabRemove
   * @param {boolean} closable
   */
  private getTitle = ({ content, onTabRemove, closable }:GET_TITLE_PROPS) => (
    <>
      <WithTooltip
        context={this}
        title={content}
        htmlTag={'div'}
      >
        <div
          // TODO This is a quick fix that should be removed with a GLDIS-1298 ticket
          title={content}
          className={'tp-tab-item-title'}
        >
          {content}
        </div>
        {closable !== false && (<>
          <PuzzleButton
            className='tp-close-button k-button-xs k-ml-1'
            name={'close'}
            fillMode={'flat'}
            iconClass={'k-i-x k-icon'}
            onClick={onTabRemove}
          />
        </>
        )
        }
      </WithTooltip>
      <PuzzleTooltip
        content={() => content}
        anchorElement={'target'}
        ref={(el: any) => this.tooltip = el}
        openDelay={1000}
        position={'bottom'}
      />
    </>
  );

  /**
   * Focuses first or last list item when during keyboard navigation (arrow left, arrow right, arrow top, arrow down) from drop down
   * @param {KeyboardEvent} e
   */
  private handleKeyUp = (e: KeyboardEvent):void => {
    if ([Keys.right, Keys.left, Keys.up, Keys.down, Keys.home, Keys.end].includes(e.keyCode) && e.target['nodeName'].toLowerCase() === 'button') {
      this.puzzleTabStripRef.current.switchTab(e, true);
    }
  }

  /**
   * Focuses drop down during keyboard navigation (arrow left, arrow right, arrow top, arrow down)
   */
  private focusOnDownDown = ():void => {
    this.dropDownButtonRef.current.reference.current.mainButton.focus();
  }

  renderBody() {
    let { selected, items } = this.state;
    const { tabStripProps } = this.props;
    const tabCount = this.numOfVisibleTitles + 1;
    const titles = items.map((item: TAB_PANEL_ITEM) => item.title).slice(tabCount, items.length);
    const dropDown_toolTip = this.translate('dropDown_toolTip');

    return (
      <PuzzleTabStrip
        ref={this.puzzleTabStripRef}
        className={'tp-tab-panel'}
        translate={this.translate}
        handleSelect={this.handleSelect}
        tabCount={tabCount}
        selected={selected}
        isTabPanel={true}
        isDropDownActive={this.isDropDownActive}
        focusOnDownDown={this.focusOnDownDown}
      >
        <TabStrip
          className={'tp-tab-strip'}
          ref={this.tabStripRef}
          {...tabStripProps}
          selected={selected}
        >
          {items.map((item, index) => {
            let { config: { closable }, config, disabled, title, uniqueItemValue, isDropDownSelector } = item;

            if (_.isFunction(this.props.onFilterAndSortChange)) {
              config = { 
                ...config, 
                onFilterAndSortChange: this.props.onFilterAndSortChange 
              };
            }

            if (this.props.dashboardGridModuleId || tabStripProps?.keepTabsMounted) {
              config = {
                ...config,
                moduleId: tabStripProps?.keepTabsMounted && config.moduleId ? config.moduleId : this.props.dashboardGridModuleId
              };
            }

            config = {
              ...config,
              onRowSelected: (dataItem, isSubRowSelected: boolean) => {
                item.config.onRowSelected?.(dataItem, isSubRowSelected);
                this.props.onRowSelected?.(dataItem, isSubRowSelected);
              },
              onRowDoubleClick: (event) => {
                item.config.onRowDoubleClick?.(event);
                this.dispatch('openDashboardDrawer');
              }
            };

            return (
              <TabStripTab
                disabled={disabled}
                title={isDropDownSelector ?
                  <>
                    <WithTooltip
                      context={this}
                      title={dropDown_toolTip}
                      htmlTag={'span'}
                      additionalProps={{ name: 'drop-down-tool-tip' }}
                    >
                      <span
                        onKeyUp={this.handleKeyUp}
                        data-cy={'tab-panel-drop-down-wrapper'}
                      >
                        <PuzzleDropDownButton
                          ref={this.dropDownButtonRef}
                          title={dropDown_toolTip}
                          items={titles}
                          size="small"
                          fillMode={'flat'}
                          className={'tab-panel-titles'}
                          iconClass={'tp-icon tp-arrow-chevron-down'}
                          onItemClick={this.handleDropDownSelect}
                          key={index}
                        />
                      </span>
                    </WithTooltip>
                    <PuzzleTooltip
                      anchorElement={'target'}
                      ref={(el: any) => this.tooltip = el}
                      openDelay={1000}
                      position={'bottom'}
                    />
                  </>
                  :
                  this.getTitle({
                    closable,
                    content: title,
                    onTabRemove: () => this.removeTab(index)
                  })}
                key={uniqueItemValue || index}
              >
                {isDropDownSelector ?
                  <div></div> :
                  <item.renderType
                    {...config}
                    key={uniqueItemValue || index}
                    shouldUseSearchBar={this.props.shouldUseSearchBar}
                    onReconfigure={this.reconfigureActiveItem}
                  />
                }
              </TabStripTab>
            );
          }
          )}
        </TabStrip>
      </PuzzleTabStrip>
    );
  }
}

export default TabPanel;
